import React, { useEffect, useState } from 'react';
import { blogPosts } from '@/data/blogPosts';
import { BlogCard, BlogPost } from '@/components/BlogCard';
import { Button } from '@/components/ui/button';
import { PenLine, Search, Info, BookmarkCheck, X, BookOpen, Building2, PlayCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogAction,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from '@/contexts/AuthContext';

// Hauptkategorien, die wir zeigen wollen
const MAIN_CATEGORIES = [
  'Bewusstes Konsumieren',
  'Sparen',
  'Minimalismus',
  'Nachhaltigkeit',
  'Finanzen'
];

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const allPosts = blogPosts;
        setPosts(allPosts);
        
        // Extrahiere alle Kategorien aus den Blog-Posts
        const categories = new Set<string>();
        allPosts.forEach(post => {
          post.categories.forEach(category => {
            categories.add(category);
          });
        });
        setAllCategories(Array.from(categories));
      } catch (error) {
        console.error('Fehler beim Laden der Blog-Einträge:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const toggleCategory = (category: string) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  const clearCategories = () => {
    setSelectedCategories([]);
  };

  const filteredPosts = posts.filter(post => {
    // Filtere nach Suchbegriff
    const matchesSearch = searchTerm.trim() === '' || 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.content?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtere nach Kategorien
    const matchesCategories = selectedCategories.length === 0 || 
      selectedCategories.some(category => post.categories.includes(category));
    
    return matchesSearch && matchesCategories;
  });

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const showAuthModal = (mode: 'login' | 'register') => {
    navigate('/auth', { state: { mode } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
      {/* Navigation - identisch zur Startseite */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <a 
              href="/" 
              className="flex items-center gap-2 hover:scale-105 transition-all duration-300 cursor-pointer group"
            >
              <img 
                src="/Favicon/favicon-196x196.png" 
                alt="KaufPause Logo" 
                className="w-8 h-8 -translate-y-0.5"
              />
              <span className="text-xl font-bold text-gray-900">
                Kauf<span className="bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent">Pause</span>
              </span>
            </a>
          </div>
          
          <div className="hidden md:flex items-center justify-center gap-8 absolute left-1/2 transform -translate-x-1/2">
            <button
              onClick={() => navigate('/blog')}
              className="text-emerald-600 bg-emerald-50 transition-colors duration-200 font-medium px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <BookOpen className="w-4 h-4" />
              Blog
            </button>
            <button
              onClick={() => navigate('/institutionen')}
              className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              Für Institutionen
            </button>
            {!user && (
              <button
                onClick={() => navigate('/demo')}
                className="text-gray-600 hover:text-emerald-600 transition-colors duration-200 font-medium hover:bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2"
              >
                <PlayCircle className="w-4 h-4" />
                Demo testen
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {user ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => signOut()}
                className="text-gray-600 hover:text-emerald-600"
              >
                Abmelden
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/auth')}
                className="text-gray-600 hover:text-emerald-600"
              >
                Anmelden
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Floating Action Buttons */}
      <div className="fixed top-24 right-6 z-40 flex flex-col gap-3">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => navigate('/blog/saved')}
                size="icon"
                className="w-12 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <BookmarkCheck className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Gespeicherte Artikel</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => navigate('/blog/create')}
                size="icon"
                className="w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <PenLine className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Artikel erstellen</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="container mx-auto px-4 pt-24 pb-16">
        {/* Header Section mit modernem Design */}
        <div className="text-center mb-12 max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            <span className="bg-gradient-to-r from-emerald-600 to-emerald-700 bg-clip-text text-transparent">
              Blog
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Tipps, Tricks und Inspiration für bewusstes Konsumieren
          </p>
          
          {/* Haftungsausschluss */}
          <div className="flex justify-center">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="link" size="sm" className="text-gray-500 text-xs hover:text-emerald-600">
                  Haftungsausschluss
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-2xl">
                <AlertDialogHeader>
                  <AlertDialogTitle>Haftungsausschluss</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-3">
                    <p>
                    Die Inhalte dieses Blogs dienen ausschließlich Informationszwecken. KaufPause übernimmt keine Haftung für die Vollständigkeit, Aktualität oder Richtigkeit der Inhalte. Die bereitgestellten Informationen stellen keine Rechtsberatung, Finanzberatung oder ähnliche Dienstleistungen dar. Bitte konsultieren Sie bei wichtigen Entscheidungen entsprechende Fachleute.
                    </p>
                    <p>
                      Erwähnte Drittanbieter-Tools stellen keine Empfehlung dar. Wir übernehmen keine Haftung für externe Services und erhalten keine Provisionen. Nutzerbeispiele können fiktiv sein. Inhalte werden unter Verwendung verschiedener Hilfsmittel erstellt und redaktionell geprüft.
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700">Verstanden</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Suche nach Artikeln..."
                className="pl-10 rounded-xl border-gray-200 focus:border-emerald-300 focus:ring-emerald-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="whitespace-nowrap rounded-xl border-gray-200 hover:border-emerald-300">
                  Kategorien
                  {selectedCategories.length > 0 && (
                    <span className="ml-2 bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5 text-xs">
                      {selectedCategories.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <DropdownMenuLabel>Kategorien auswählen</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {MAIN_CATEGORIES.map((category) => (
                  <DropdownMenuItem 
                    key={category}
                    onClick={() => toggleCategory(category)}
                    className="flex justify-between items-center cursor-pointer"
                  >
                    {category}
                    {selectedCategories.includes(category) && (
                      <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                    )}
                  </DropdownMenuItem>
                ))}
                {selectedCategories.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={clearCategories}
                      className="text-red-500 cursor-pointer flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      Filter zurücksetzen
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {/* Angezeigte Kategoriefilter */}
          {selectedCategories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {selectedCategories.map(category => (
                <div 
                  key={category}
                  className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-xl text-sm flex items-center gap-1 border border-emerald-200"
                >
                  {category}
                  <button 
                    onClick={() => toggleCategory(category)}
                    className="hover:bg-emerald-100 rounded-full h-5 w-5 flex items-center justify-center transition-colors duration-200"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <button 
                className="text-gray-500 hover:text-gray-700 text-sm underline transition-colors duration-200"
                onClick={clearCategories}
              >
                Alle zurücksetzen
              </button>
            </div>
          )}
        </div>

        {/* Grid of Blog Posts */}
        {loading ? (
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse border border-gray-100">
                <div className="h-48 bg-gray-200"></div>
                <div className="p-6">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
                  <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="max-w-lg mx-auto text-center bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-xl font-medium text-gray-900 mb-2">Keine Artikel gefunden</h3>
            <p className="text-gray-600 mb-6">
              Es wurden keine Artikel gefunden, die deinen Filterkriterien entsprechen.
            </p>
            <Button 
              onClick={() => {
                setSearchTerm('');
                setSelectedCategories([]);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            >
              Filter zurücksetzen
            </Button>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post, index) => (
              <BlogCard key={index} post={post} />
            ))}
          </div>
        )}

        {/* Call to Action - Eigenen Artikel schreiben */}
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-8 rounded-2xl shadow-sm mt-16 border border-emerald-100">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                Eigenen Artikel verfassen?
              </h3>
              <p className="text-gray-600">
                Du hast Tipps zum bewussten Konsumieren oder möchtest deine Erfahrungen teilen? 
                Erstelle deinen eigenen Artikel im Markdown-Format und teile ihn mit der Community.
              </p>
            </div>
            <Button 
              size="lg" 
              onClick={() => navigate('/blog/create')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 whitespace-nowrap rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
            >
              <PenLine className="h-5 w-5" />
              Artikel verfassen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog; 